import type { Lap } from "./types";

/**
 * Cuts a session into stints and prices each one in fuel, rubber and lap time.
 *
 * A stint is the unit a run is actually planned in, and it is what makes the
 * consumable figures mean anything: fuel used over a session that was refuelled
 * twice is a number about the pit crew, while fuel per lap within a stint is a
 * number about the driver. The same goes for tyres, and it is the only honest way
 * to talk about degradation at all, since a fresh set resets the clock.
 *
 * The boundary comes from LMU's own pit-lane flag, already reduced to a pair of
 * out/in markers on each lap at ingest, so nothing here has to guess from lap
 * times where a stop happened.
 */
export interface Stint {
  /** 1-based, in the order they were run. */
  number: number;
  laps: Lap[];
  firstLap: number;
  lastLap: number;
  /** Laps that were both complete and clean, which is all the trend can use. */
  timedLaps: number;
  bestLapMs: number | null;
  /**
   * Seconds gained or lost per lap across the stint, from a least-squares fit.
   * Positive means it slowed down. Null when there is too little to fit.
   */
  degradationSPerLap: number | null;
  fuelUsedL: number | null;
  fuelPerLapL: number | null;
  /** Tread used across the stint, and what was left at the end of it. */
  tyreUsedPct: number | null;
  tyreLeftPct: number | null;
  /** Set when this stint started on a different compound than the one before. */
  compoundChanged: boolean;
  compound: number | null;
}

/** Fewest clean laps a degradation figure can honestly be fitted to. */
const MIN_LAPS_FOR_TREND = 4;

export function splitStints(laps: Lap[]): Stint[] {
  const ordered = [...laps].sort((a, b) => a.lap_number - b.lap_number);
  if (ordered.length === 0) return [];

  const groups: Lap[][] = [];
  let current: Lap[] = [];

  for (const lap of ordered) {
    // An out lap opens a stint; an in lap closes the one it belongs to. A lap that
    // is both is a drive-through, and lands in its own group either way.
    if (lap.is_out_lap && current.length > 0) {
      groups.push(current);
      current = [];
    }
    current.push(lap);
    if (lap.is_in_lap) {
      groups.push(current);
      current = [];
    }
  }
  if (current.length > 0) groups.push(current);

  let previousCompound: number | null = null;

  return groups.map((group, index) => {
    const clean = group.filter(
      (lap) => lap.lap_time_ms != null && lap.is_valid !== false,
    );
    const compound = firstNotNull(group.map((lap) => lap.tyre_compound));
    const stint: Stint = {
      number: index + 1,
      laps: group,
      firstLap: group[0].lap_number,
      lastLap: group[group.length - 1].lap_number,
      timedLaps: clean.length,
      bestLapMs:
        clean.length > 0 ? Math.min(...clean.map((lap) => lap.lap_time_ms as number)) : null,
      degradationSPerLap: trend(clean),
      fuelUsedL: total(group.map((lap) => lap.fuel_used_l)),
      fuelPerLapL: null,
      tyreUsedPct: total(group.map((lap) => lap.tyre_used_pct)),
      tyreLeftPct: lastNotNull(group.map((lap) => lap.tyre_left_pct)),
      compoundChanged: index > 0 && compound !== null && compound !== previousCompound,
      compound,
    };

    if (stint.fuelUsedL !== null && group.length > 0) {
      stint.fuelPerLapL = stint.fuelUsedL / group.length;
    }
    if (compound !== null) previousCompound = compound;
    return stint;
  });
}

/**
 * Least-squares slope of lap time against lap number, in seconds per lap.
 *
 * Deliberately the whole stint rather than first-versus-last: a single traffic
 * lap at the end would otherwise set the entire degradation figure, and the
 * question being asked is what the trend is, not what the extremes were.
 */
function trend(laps: Lap[]): number | null {
  if (laps.length < MIN_LAPS_FOR_TREND) return null;

  const n = laps.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;

  laps.forEach((lap, index) => {
    const y = (lap.lap_time_ms as number) / 1000;
    sumX += index;
    sumY += y;
    sumXY += index * y;
    sumXX += index * index;
  });

  const denominator = n * sumXX - sumX * sumX;
  if (denominator === 0) return null;
  return (n * sumXY - sumX * sumY) / denominator;
}

function total(values: Array<number | null>): number | null {
  const present = values.filter((value): value is number => value !== null);
  return present.length > 0 ? present.reduce((sum, value) => sum + value, 0) : null;
}

function firstNotNull(values: Array<number | null>): number | null {
  return values.find((value): value is number => value !== null) ?? null;
}

function lastNotNull(values: Array<number | null>): number | null {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index];
    if (value !== null) return value;
  }
  return null;
}
